import { has, includes, isEmpty, snakeCase, sum, toUpper, uniq } from 'lodash';
import { analyticsDataLayer } from '../../utils/analytics-helper';
import { clearAARCache } from '../executeRedemption/executeRedemption.module';
import { ExchangeTransactionActionType as act } from '../exchangeTransaction';
import { formatUnixDate } from '../../../utils/dates/date-utility';
import { scrollElementIntoFullView } from '../../../utils/dom-utils';
import { isIRA } from 'src/utils/IRA/IRA-utility';
import {
  createApiPostActionCreator,
  createExchangeTransaction,
} from '../../utils/module-util';
import { coalesce, hasValue } from '../../../utils/utils';

const VALIDATE_EXCHANGE = 'VALIDATE_EXCHANGE';
const EXECUTE_EXCHANGE = 'EXECUTE_EXCHANGE';
const EMAIL_EXCHANGE_CONFIRMATION = 'EMAIL_EXCHANGE_CONFIRMATION';

const DEFAULT_STATE = {
  confirmationNumber: '',
  error: null,
  hasFailed: false,
  isLoaded: false,
  isLoading: false,
  isValid: false,
  isValidated: false,
  isValidating: false,
  tradeDate: '',
  transactions: [],
  validationMessages: [],
  exchangeTradeDates: [],
};

const hasTimedOut = (payload) => {
  try {
    return (
      has(payload, 'text') &&
      includes(payload.text, '{') &&
      JSON.parse(payload.text).error === 'timeout'
    );
  } catch (e) {
    return false;
  }
};
const printErrorMessage = (payloadBody) => {
  return has(payloadBody, 'message') ? payloadBody.message : '';
};

export default function executeExchange(
  state = DEFAULT_STATE,
  { payload, type }
) {
  switch (type) {
    case `${EXECUTE_EXCHANGE}_PENDING`:
      return {
        ...state,
        isLoaded: false,
        isLoading: true,
        transactions: [],
      };
    case `${EXECUTE_EXCHANGE}_FULFILLED`: {
      const { body } = payload; // payload for Exchange is very different from buy/sell, see mock _base/pi-transaction-exchange-service-v1-api-pi-transaction-exchange-service-v1-api-execute.json
      let confirmationNumber = '';
      let tradeDate = '';

      if (Array.isArray(body) && hasValue(body)) {
        const firstTransaction = body[0];
        confirmationNumber = coalesce(firstTransaction.confirmationNumber, '');
        tradeDate = firstTransaction.tradeDate
          ? formatUnixDate(firstTransaction.tradeDate)
          : '';
        clearAARCache();
      }
      return {
        ...state,
        isLoaded: true,
        isLoading: false,
        hasFailed: false,
        transactions: body,
        confirmationNumber,
        tradeDate,
      };
    }
    case `${EXECUTE_EXCHANGE}_REJECTED`:
      return {
        ...state,
        isLoaded: true,
        isLoading: false,
        hasFailed: true,
        timeout: hasTimedOut(payload),
        message: printErrorMessage(payload.body),
        error: payload.body,
      };

    // clear validation on any change that would affect validation
    case act.DESELECT_EXCHANGE_FROM_FUND:
    case act.SELECT_EXCHANGE_FROM_FUND:
    case act.UPDATE_EXCHANGE_FROM_FUND:
    case act.DESELECT_EXCHANGE_TO_FUND:
    case act.SELECT_EXCHANGE_TO_FUND:
    case act.UPDATE_EXCHANGE_TO_FUND:
    case act.ADD_NEW_EXCHANGE_TO_FUND:
      return !state.isValidated &&
        !state.isValid &&
        isEmpty(state.validationMessages)
        ? state
        : {
            ...state,
            isValidated: false,
            isValid: false,
            validationMessages: [],
          };

    case `${VALIDATE_EXCHANGE}_PENDING`:
      return {
        ...state,
        isValidated: false,
        isValidating: true,
        isValid: false,
        validationMessages: [],
      };
    case `${VALIDATE_EXCHANGE}_FULFILLED`: {
      const {
        messages: validationMessages,
        valid: isValid,
        estimatedTradeDate,
      } = payload.body;
      return {
        ...state,
        isValidated: true,
        isValidating: false,
        isValid,
        validationMessages,
        exchangeTradeDates: estimatedTradeDate,
      };
    }
    case `${VALIDATE_EXCHANGE}_REJECTED`:
      return {
        ...state,
        isValidated: true,
        isValidating: false,
        isValid: false,
        validationMessages: [
          `<span>
              We’re unable to process your request. Please
            <a
              className="exclude"
              href="https://www.troweprice.com/personal-investing/help/contact/index.html"
              rel="noreferrer"
              target="_blank"
            >
              Contact Us
            </a>
            .
          </span>`,
        ],
      };
    default:
      return state;
  }
}

export function validateExchange(
  exchangeContributionYear,
  exchangeFederalWithholdingApplied,
  exchangeFederalWithholdingPercent,
  exchangeFromSummaryFundList,
  exchangeToSummaryFundList,
  exchangeType
) {
  const body = createExchangeTransaction(
    exchangeContributionYear,
    exchangeFederalWithholdingApplied &&
      isIRA(exchangeFromSummaryFundList[0]) &&
      !isIRA(exchangeToSummaryFundList[0])
      ? Number(exchangeFederalWithholdingPercent)
      : 0,
    exchangeFromSummaryFundList,
    exchangeToSummaryFundList,
    exchangeType
  );
  const validator = createApiPostActionCreator(
    VALIDATE_EXCHANGE,
    'exchangeValidate',
    body[0]
  );
  return (dispatch) => validator(dispatch);
}

export function submitExchange(
  exchangeContributionYear,
  exchangeFederalWithholdingApplied,
  exchangeFederalWithholdingPercent,
  exchangeFromSummaryFundList,
  exchangeToSummaryFundList,
  exchangeType
) {
  scrollElementIntoFullView('buy-page-container');
  const body = createExchangeTransaction(
    exchangeContributionYear,
    exchangeFederalWithholdingApplied &&
      isIRA(exchangeFromSummaryFundList[0]) &&
      !isIRA(exchangeToSummaryFundList[0])
      ? exchangeFederalWithholdingPercent
      : 0,
    exchangeFromSummaryFundList,
    exchangeToSummaryFundList,
    exchangeType
  );

  try {
    const amount = sum(
      body.map((transaction) => {
        const { transactionRecords } = transaction;
        return sum(transactionRecords.map((tr) => tr.amount));
      })
    );

    let accountTypes = [];
    let quantity = 0;
    body.forEach((transaction) => {
      const { transactionRecords } = transaction;
      if (!transactionRecords) {
        return;
      }
      quantity = transactionRecords.length;
      return transactionRecords.forEach((tr) => {
        const { sourcePositionKey, targetPositionKey } = tr;
        if (sourcePositionKey) {
          const {
            agreementKey: {
              productType: { enumerationName },
            },
          } = sourcePositionKey;
          if (enumerationName) {
            accountTypes = accountTypes.concat(
              toUpper(snakeCase(enumerationName))
            );
          }
        }
        if (targetPositionKey) {
          const {
            agreementKey: {
              productType: { enumerationName },
            },
          } = targetPositionKey;
          if (enumerationName) {
            accountTypes = accountTypes.concat(
              toUpper(snakeCase(enumerationName))
            );
          }
        }
      });
    });

    analyticsDataLayer({
      quantity,
      amount,
      accountTypes: uniq(accountTypes),
    });
  } catch (e) {
    // don't let analytics errors cause other things to fail
  }

  return createApiPostActionCreator(EXECUTE_EXCHANGE, 'exchangeExecute', body);
}

export function emailExchangeConfirmation(transactions) {
  const transactionsWithConfirms = transactions.filter(
    (t) => t.confirmationNumber.length
  );
  const emailBody = {
    lineOfBusiness: {
      enumerationID: 10,
      enumerationName: 'retail',
    },
    processor: {
      enumerationID: 100,
      enumerationName: 'client',
    },
    status: {
      enumerationID: 100,
      enumerationName: 'success',
    },
    webApplication: {
      agent: '007',
      '@Type': 'WebApplication',
    },
    runtimeIdentifier: {
      name: 'AAW-AAR',
      domain: 'RETE',
      hostname: 'self-serve-ui',
    },
    transactions: transactionsWithConfirms,
  };

  return createApiPostActionCreator(
    EMAIL_EXCHANGE_CONFIRMATION,
    'exchangeEmailConfirmation',
    emailBody
  );
}

/*

Sample requests:


--------------
--------------   One-to-one
--------------

[
  {
    "@Type":"Transaction",
    "transactionID": "generated",
    "transactionRecords": [{
      "@Type":"AarTransactionRecord",
      "unit": {
        "enumerationID": 1,
        "enumerationName": "Dollars",
        "code":"D"
      },
      "subType":{
        "enumerationID": 1033,
        "enumerationName": "ExistingAccount"
      },
      "sourcePositionKey": {
        "@Type":"PositionKey",
        "investmentVehicleKey": {
          "@Type":"InvestmentVehicleKey",
          "ticker": "PRFDX",
          "trpcode": "EIF"
        },
        "agreementKey": {
          "agreementID": "29944533",
          "agreementNumber": "7100030428",
          "checkSum": 5,
          "productType": {
            "enumerationID": 10,
            "enumerationName": "MutualFund"
          },
          "@Type":"MutualFundAgreementKey"
        }
      },
      "targetPositionKey": {
        "@Type":"PositionKey",
        "investmentVehicleKey": {
          "@Type":"InvestmentVehicleKey",
          "ticker": "TSCXX",
          "trpcode": "CSC"
        },
        "agreementKey": {
          "agreementID": "29944503",
          "agreementNumber": "5070000806",
          "checkSum": 4,
          "productType": {
            "enumerationID": 80,
            "enumerationName": "National529"
          },
          "@Type":"MutualFundAgreementKey"
        }
      },
      "amount": 147.07
    }]
  }
]

  Sample 2 :

[
  {
    "@Type":"Transaction",
    "contributionYear":{
      "enumerationID": 101,
      "enumerationName": "PriorYear"
    },
    "transactionID": "generated",
    "transactionRecords": [{
      "@Type":"AarTransactionRecord",
      "unit": {
        "enumerationID": 1,
        "enumerationName": "Dollars",
        "code":"D"
      },
      "subType":{
        "enumerationID": 1033,
        "enumerationName": "ExistingAccount"
      },
      "sourcePositionKey": {
        "@Type":"PositionKey",
        "investmentVehicleKey": {
          "@Type":"InvestmentVehicleKey",
          "ticker": "PRFDX",
          "trpcode": "EIF"
        },
        "agreementKey": {
          "agreementID": "29944533",
          "agreementNumber": "7100030428",
          "checkSum": 5,
          "productType": {
            "enumerationID": 10,
            "enumerationName": "MutualFund"
          },
          "@Type":"MutualFundAgreementKey"
        }
      },
      "targetPositionKey": {
        "@Type":"PositionKey",
        "investmentVehicleKey": {
          "@Type":"InvestmentVehicleKey",
          "ticker": "TRRCX",
          "trpcode": "RPC"
        },
        "agreementKey": {
          "agreementID": "29944504",
          "agreementNumber": "1420005195",
          "checkSum": 5,
          "productType": {
            "enumerationID": 10,
            "enumerationName": "MutualFund"
          },
          "@Type":"MutualFundAgreementKey"
        }
      },
      "amount": 147.07
    }]
  }
]


--------------
--------------   One-to-many
--------------

[{
  "@Type":"Transaction",
  "contributionYear":{
    "enumerationID": 101,
    "enumerationName": "PriorYear"
  },
  "transactionID": "generated",
  "transactionRecords": [{
    "@Type":"AarTransactionRecord",
    "unit": {
      "enumerationID": 1,
      "enumerationName": "Dollars",
      "code":"D"
    },
    "subType":{
      "enumerationID": 1033,
      "enumerationName": "ExistingAccount"
    },
    "sourcePositionKey": {
      "@Type":"PositionKey",
      "investmentVehicleKey": {
        "@Type":"InvestmentVehicleKey",
        "ticker": "PRFDX",
        "trpcode": "EIF"
      },
      "agreementKey": {
        "agreementID": "29944533",
        "agreementNumber": "7100030428",
        "checkSum": 5,
        "productType": {
          "enumerationID": 10,
          "enumerationName": "MutualFund"
        },
        "@Type":"MutualFundAgreementKey"
      }
    },
    "targetPositionKey": {
      "@Type":"PositionKey",
      "investmentVehicleKey": {
        "@Type":"InvestmentVehicleKey",
        "ticker": "TRRCX",
        "trpcode": "RPC"
      },
      "agreementKey": {
        "agreementID": "29944504",
        "agreementNumber": "1420005195",
        "checkSum": 5,
        "productType": {
          "enumerationID": 10,
          "enumerationName": "MutualFund"
        },
        "@Type":"MutualFundAgreementKey"
      }
    },
    "amount": 147.07
  },{
    "@Type":"AarTransactionRecord",
    "unit": {
      "enumerationID": 1,
      "enumerationName": "Dollars",
      "code":"D"
    },
    "subType":{
      "enumerationID": 1033,
      "enumerationName": "ExistingAccount"
    },
    "sourcePositionKey": {
      "@Type":"PositionKey",
      "investmentVehicleKey": {
        "@Type":"InvestmentVehicleKey",
        "ticker": "PRFDX",
        "trpcode": "EIF"
      },
      "agreementKey": {
        "agreementID": "29944533",
        "agreementNumber": "7100030428",
        "checkSum": 5,
        "productType": {
          "enumerationID": 10,
          "enumerationName": "MutualFund"
        },
        "@Type":"MutualFundAgreementKey"
      }
    },
    "targetPositionKey": {
      "@Type":"PositionKey",
      "investmentVehicleKey": {
        "@Type":"InvestmentVehicleKey",
        "ticker": "TSCXX",
        "trpcode": "CSC"
      },
      "agreementKey": {
        "agreementID": "29944503",
        "agreementNumber": "5070000806",
        "checkSum": 4,
        "productType": {
          "enumerationID": 80,
          "enumerationName": "National529"
        },
        "@Type":"MutualFundAgreementKey"
      }
    },
    "amount": 147.07
  }]
}]



--------------
--------------   Many-to-one
--------------

[{
  "@Type":"Transaction",
  "contributionYear":{
    "enumerationID": 101,
    "enumerationName": "PriorYear"
  },
  "transactionID": "generated",
  "transactionRecords": [{
    "@Type":"AarTransactionRecord",
    "unit": {
      "enumerationID": 1,
      "enumerationName": "Dollars",
      "code":"D"
    },
    "subType":{
      "enumerationID": 1033,
      "enumerationName": "ExistingAccount"
    },
    "sourcePositionKey": {
      "@Type":"PositionKey",
      "investmentVehicleKey": {
        "@Type":"InvestmentVehicleKey",
        "ticker": "PRFDX",
        "trpcode": "EIF"
      },
      "agreementKey": {
        "agreementID": "29944504",
        "agreementNumber": "1420005195",
        "checkSum": 5,
        "productType": {
          "enumerationID": 10,
          "enumerationName": "MutualFund"
        },
        "@Type":"MutualFundAgreementKey"
      }
    },
    "targetPositionKey": {
      "@Type":"PositionKey",
      "investmentVehicleKey": {
        "@Type":"InvestmentVehicleKey",
        "ticker": "TRRCX",
        "trpcode": "RPC"
      },
      "agreementKey": {
        "agreementID": "29944533",
        "agreementNumber": "7100030428",
        "checkSum": 5,
        "productType": {
          "enumerationID": 10,
          "enumerationName": "MutualFund"
        },
        "@Type":"MutualFundAgreementKey"
      }
    },
    "amount": 147.07
  },{
    "@Type":"AarTransactionRecord",
    "unit": {
      "enumerationID": 1,
      "enumerationName": "Dollars",
      "code":"D"
    },
    "subType":{
      "enumerationID": 1033,
      "enumerationName": "ExistingAccount"
    },
    "sourcePositionKey": {
      "@Type":"PositionKey",
      "investmentVehicleKey": {
        "@Type":"InvestmentVehicleKey",
        "ticker": "PRFDX",
        "trpcode": "EIF"
      },
      "agreementKey": {
        "agreementID": "29944503",
        "agreementNumber": "5070000806",
        "checkSum": 4,
        "productType": {
          "enumerationID": 80,
          "enumerationName": "National529"
        },
        "@Type":"MutualFundAgreementKey"
      }
    },
    "targetPositionKey": {
      "@Type":"PositionKey",
      "investmentVehicleKey": {
        "@Type":"InvestmentVehicleKey",
        "ticker": "TSCXX",
        "trpcode": "CSC"
      },
      "agreementKey": {
        "agreementID": "29944533",
        "agreementNumber": "7100030428",
        "checkSum": 5,
        "productType": {
          "enumerationID": 10,
          "enumerationName": "MutualFund"
        },
        "@Type":"MutualFundAgreementKey"
      }
    },
    "amount": 147.07
  }]
}]

 */
